import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { useAuth } from '../AuthContext';
import usePermissions from '../../common/usePermissions';
import { rootUrl } from '../../../config/config';
import { formatMoney } from '../owners/formatMoney';
import { FaSearch, FaFileUpload, FaFileCsv, FaMobileAlt } from 'react-icons/fa';
import C2BTransactionDrawer from './C2BTransactionDrawer';
import C2BBulkPostModal from './C2BBulkPostModal';
import C2BRecoveryImportModal from './C2BRecoveryImportModal';
import C2BLookupPanel from './C2BLookupPanel';
import { buildC2BTransactionsCsv, downloadCsv, fetchAllC2BTransactions, C2B_EXPORT_MAX_ROWS } from './c2bExport';

const LIMIT = 100;
const FILTER_CACHE_KEY = 'c2b-transactions-filters';
const C2B_LOOKUP_PERMISSION = 'payments.c2b.lookup';
const C2B_RECOVERY_IMPORT_PERMISSION = 'payments.c2b.recovery_import';

function loadCachedFilters() {
    if (typeof window === 'undefined') return {};
    try {
        const raw = window.localStorage.getItem(FILTER_CACHE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function saveCachedFilters(filters) {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(FILTER_CACHE_KEY, JSON.stringify(filters));
    } catch {
        // Ignore storage failures; filters still work in-memory.
    }
}

function formatDateTime(ts) {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '36px',
        borderColor: state.isFocused ? '#FF5733' : '#d1d5db',
        boxShadow: 'none',
        '&:hover': { borderColor: '#FF5733' },
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: '36px' }),
    clearIndicator: (base) => ({ ...base, padding: '4px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    menu: (base) => ({ ...base, zIndex: 50 }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#fff3f0' : 'white',
        color: state.isSelected ? 'white' : '#1f2937',
    }),
};

const C2BTransactions = () => {
    const { token } = useAuth();
    const { hasPermission } = usePermissions();
    const authToken = token || localStorage.getItem('authToken') || '';
    const cachedFilters = useMemo(() => loadCachedFilters(), []);

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [summary, setSummary] = useState(null);
    const [offset, setOffset] = useState(0);
    const [weeks, setWeeks] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState(cachedFilters.selectedWeek || '');
    const [from, setFrom] = useState(cachedFilters.from || '');
    const [to, setTo] = useState(cachedFilters.to || '');
    const [matchStatus, setMatchStatus] = useState(cachedFilters.matchStatus || '');
    const [postStatus, setPostStatus] = useState(cachedFilters.postStatus || '');
    const [cbPosted, setCbPosted] = useState(cachedFilters.cbPosted || '');
    const [search, setSearch] = useState(cachedFilters.search || '');
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkPostOpen, setBulkPostOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [exporting, setExporting] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showRecoveryImport, setShowRecoveryImport] = useState(false);
    const [showLookup, setShowLookup] = useState(false);
    const canLookup = hasPermission(C2B_LOOKUP_PERMISSION) || hasPermission('roles.manage');
    const canRecover = hasPermission(C2B_RECOVERY_IMPORT_PERMISSION) || hasPermission('roles.manage');

    const weekOptions = useMemo(() => weeks.map((week) => {
        const weekValue = String(week.week ?? week.week_key ?? week.id);
        const start = week.start_date ? new Date(week.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
        const end = week.end_date ? new Date(week.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
        return {
            value: weekValue,
            label: `${week.week || week.week_key || weekValue} (${start} - ${end})`,
        };
    }), [weeks]);

    const matchStatusOptions = useMemo(() => ([
        { value: '', label: 'All' },
        { value: 'matched', label: 'Matched' },
        { value: 'unmatched', label: 'Unmatched' },
    ]), []);

    const postStatusOptions = useMemo(() => ([
        { value: '', label: 'All' },
        { value: 'posted', label: 'Posted' },
        { value: 'pending', label: 'Not Posted' },
    ]), []);

    const cbPostedOptions = useMemo(() => ([
        { value: '', label: 'All' },
        { value: 'posted', label: 'Posted' },
        { value: 'pending', label: 'Pending' },
    ]), []);

    const fetchWeeks = useCallback(async () => {
        try {
            const res = await axios.get(`${rootUrl}/system/weeks`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            const data = res.data?.data ?? res.data ?? [];
            setWeeks(Array.isArray(data) ? data : []);
        } catch {
            setWeeks([]);
        }
    }, [authToken]);

    const fetchData = useCallback(async (newOffset = 0) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ limit: LIMIT, offset: newOffset });
            if (from) params.set('from', from);
            if (to) params.set('to', to);
            if (matchStatus) params.set('match_status', matchStatus);
            if (postStatus) params.set('post_status', postStatus);
            if (cbPosted) params.set('cb_posted', cbPosted);
            if (search) params.set('search', search);
            const res = await axios.get(`${rootUrl}/api/payments/c2b?${params}`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            setRows(res.data.data || []);
            setTotal(res.data.total || 0);
            setTotalAmount(Number(res.data.total_amount) || 0);
            setSummary(res.data.summary || null);
            setOffset(newOffset);
            setSelectedIds([]);
        } catch {
            setError('Failed to load C2B transactions. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [from, to, matchStatus, postStatus, cbPosted, search, authToken]);

    useEffect(() => { fetchWeeks(); }, [fetchWeeks]);
    useEffect(() => { fetchData(0); }, [fetchData]);

    const handleReset = () => {
        setSelectedWeek('');
        setFrom('');
        setTo('');
        setMatchStatus('');
        setPostStatus('');
        setCbPosted('');
        setSearch('');
        setSelectedIds([]);
        setOffset(0);
        setTotalAmount(0);
        saveCachedFilters({
            selectedWeek: '',
            from: '',
            to: '',
            matchStatus: '',
            postStatus: '',
            cbPosted: '',
            search: '',
        });
    };

    const handleWeekChange = (option) => {
        const value = option?.value || '';
        setSelectedWeek(value);

        if (!value) {
            setFrom('');
            setTo('');
            setSelectedIds([]);
            setOffset(0);
            return;
        }

        const selected = weeks.find((week) => String(week.week ?? week.week_key) === value);
        setFrom(selected?.start_date || '');
        setTo(selected?.end_date || '');
        setSelectedIds([]);
        setOffset(0);
    };

    const handleFromChange = (e) => {
        setSelectedWeek('');
        setFrom(e.target.value);
        setSelectedIds([]);
        setOffset(0);
    };

    const handleToChange = (e) => {
        setSelectedWeek('');
        setTo(e.target.value);
        setSelectedIds([]);
        setOffset(0);
    };

    const handleMatchStatusChange = (option) => {
        setMatchStatus(option?.value || '');
        setSelectedIds([]);
        setOffset(0);
    };

    const handlePostStatusChange = (option) => {
        setPostStatus(option?.value || '');
        setSelectedIds([]);
        setOffset(0);
    };

    const handleCbPostedChange = (option) => {
        setCbPosted(option?.value || '');
        setSelectedIds([]);
        setOffset(0);
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
        setSelectedIds([]);
        setOffset(0);
    };

    useEffect(() => {
        saveCachedFilters({
            selectedWeek,
            from,
            to,
            matchStatus,
            postStatus,
            cbPosted,
            search,
        });
    }, [selectedWeek, from, to, matchStatus, postStatus, cbPosted, search]);

    const selectableRowIds = useMemo(() => {
        return rows
            .filter((row) => row.cb_posted !== 'posted')
            .map((row) => row.id);
    }, [rows]);

    const selectedRowMap = useMemo(() => new Set(selectedIds), [selectedIds]);
    const allSelectableSelected = selectableRowIds.length > 0 && selectableRowIds.every((id) => selectedRowMap.has(id));
    const someSelectableSelected = selectableRowIds.some((id) => selectedRowMap.has(id));
    const selectedRows = rows.filter((row) => selectedRowMap.has(row.id));

    const toggleRowSelection = (row) => {
        if (row.cb_posted === 'posted') return;
        setSelectedIds((prev) => (
            prev.includes(row.id)
                ? prev.filter((id) => id !== row.id)
                : [...prev, row.id]
        ));
    };

    const toggleSelectAllOnPage = () => {
        if (allSelectableSelected) {
            setSelectedIds((prev) => prev.filter((id) => !selectableRowIds.includes(id)));
            return;
        }
        setSelectedIds((prev) => Array.from(new Set([...prev, ...selectableRowIds])));
    };

    const handleBulkPostSuccess = () => {
        setBulkPostOpen(false);
        setSelectedIds([]);
        setSelectedRow(null);
        fetchData(offset);
    };

    const handleRecoveryImported = () => {
        fetchData(offset);
        setShowRecoveryImport(false);
    };

    const handleExport = async () => {
        setExporting(true);
        setError('');
        try {
            const params = {};
            if (from) params.from = from;
            if (to) params.to = to;
            if (matchStatus) params.match_status = matchStatus;
            if (postStatus) params.post_status = postStatus;
            if (cbPosted) params.cb_posted = cbPosted;
            if (search) params.search = search;

            const { rows: exportRows, truncated } = await fetchAllC2BTransactions({
                baseUrl: rootUrl,
                headers: { Authorization: `Bearer ${authToken}` },
                params,
            });

            if (exportRows.length === 0) {
                setError('No C2B transactions match the current filters, so there is nothing to export.');
                return;
            }

            const csv = buildC2BTransactionsCsv(exportRows);
            const stamp = new Date().toISOString().split('T')[0];
            downloadCsv(csv, `c2b-transactions-${stamp}.csv`);

            if (truncated) {
                setError(`Export limited to the first ${C2B_EXPORT_MAX_ROWS.toLocaleString()} rows. Narrow the filters to export the rest.`);
            }
        } catch {
            setError('Failed to export C2B transactions. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <>
        <div className="p-4 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-4">
                <div>
                    <div className="flex items-center gap-2">
                        <FaMobileAlt className="text-[#FF5733]" />
                        <h1 className="text-2xl font-bold text-gray-900">C2B Transactions</h1>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Review inbound M-Pesa paybill payments and post them to cars.</p>
                </div>
                <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                    <button
                        type="button"
                        onClick={handleExport}
                        disabled={exporting || loading || total === 0}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[#FF5733] text-[#FF5733] hover:bg-[#fff3f0] text-sm font-medium transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <FaFileCsv />
                        <span className="hidden md:inline">{exporting ? 'Exporting…' : 'Export CSV'}</span>
                        <span className="md:hidden">{exporting ? '…' : 'Export'}</span>
                    </button>
                    {canLookup && (
                        <button
                            type="button"
                            onClick={() => setShowLookup(v => !v)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-medium transition-colors shadow-sm"
                        >
                            <FaSearch />
                            <span className="hidden md:inline">Lookup Transaction</span>
                            <span className="md:hidden">Lookup</span>
                        </button>
                    )}
                    {canRecover && (
                        <button
                            type="button"
                            onClick={() => setShowRecoveryImport(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-medium transition-colors shadow-sm"
                        >
                            <FaFileUpload />
                            Recover from CSV
                        </button>
                    )}
                </div>
            </div>

            {showLookup && (
                <C2BLookupPanel
                    authToken={authToken}
                    onClose={() => setShowLookup(false)}
                    onImported={() => fetchData(offset)}
                />
            )}

            {/* Filters */}
            <div className="bg-white rounded-xl shadow p-4 mb-4 flex flex-wrap items-end gap-3">
                <div>
                    <label htmlFor="c2b-week" className="block text-xs font-medium text-gray-600 mb-1">Week</label>
                    <Select
                        id="c2b-week"
                        inputId="c2b-week"
                        value={weekOptions.find((option) => option.value === selectedWeek) || null}
                        onChange={handleWeekChange}
                        options={weekOptions}
                        styles={selectStyles}
                        className="min-w-[220px]"
                        classNamePrefix="react-select"
                        isClearable
                        isSearchable={false}
                        placeholder="All Weeks"
                    />
                </div>
                <div>
                    <label htmlFor="c2b-from" className="block text-xs font-medium text-gray-600 mb-1">From</label>
                    <input id="c2b-from" type="date" value={from} onChange={handleFromChange}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#FF5733]" />
                </div>
                <div>
                    <label htmlFor="c2b-to" className="block text-xs font-medium text-gray-600 mb-1">To</label>
                    <input id="c2b-to" type="date" value={to} onChange={handleToChange}
                        className="px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#FF5733]" />
                </div>
                <div>
                    <label htmlFor="c2b-match-status" className="block text-xs font-medium text-gray-600 mb-1">Car Post Status</label>
                    <Select
                        inputId="c2b-match-status"
                        value={matchStatusOptions.find((option) => option.value === matchStatus) || null}
                        onChange={handleMatchStatusChange}
                        options={matchStatusOptions}
                        styles={selectStyles}
                        className="min-w-[160px]"
                        classNamePrefix="react-select"
                        isClearable
                        isSearchable={false}
                        placeholder="All"
                    />
                </div>
                <div>
                    <label htmlFor="c2b-post-status" className="block text-xs font-medium text-gray-600 mb-1">Post Status</label>
                    <Select
                        inputId="c2b-post-status"
                        value={postStatusOptions.find((option) => option.value === postStatus) || null}
                        onChange={handlePostStatusChange}
                        options={postStatusOptions}
                        styles={selectStyles}
                        className="min-w-[160px]"
                        classNamePrefix="react-select"
                        isClearable
                        isSearchable={false}
                        placeholder="All"
                    />
                </div>
                <div>
                    <label htmlFor="c2b-cb-posted" className="block text-xs font-medium text-gray-600 mb-1">Cashbook Status</label>
                    <Select
                        inputId="c2b-cb-posted"
                        value={cbPostedOptions.find((option) => option.value === cbPosted) || null}
                        onChange={handleCbPostedChange}
                        options={cbPostedOptions}
                        styles={selectStyles}
                        className="min-w-[160px]"
                        classNamePrefix="react-select"
                        isClearable
                        isSearchable={false}
                        placeholder="All"
                    />
                </div>
                <div>
                    <label htmlFor="c2b-search" className="block text-xs font-medium text-gray-600 mb-1">Search</label>
                    <div className="relative">
                        <FaSearch style={{ position: 'absolute', left: '8px', top: '9px', color: '#9ca3af', fontSize: '13px' }} />
                        <input id="c2b-search" type="text" value={search} onChange={handleSearchChange}
                            placeholder="Receipt, plate, ref…"
                            className="pl-7 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#FF5733]" />
                    </div>
                </div>
                <button onClick={handleReset} className="text-sm text-[#FF5733] hover:text-[#e64520] font-medium pb-1">
                    Reset
                </button>
            </div>

            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-gray-600">
                    {selectedIds.length > 0
                        ? `${selectedIds.length} row${selectedIds.length === 1 ? '' : 's'} selected`
                        : 'Select rows that are not yet posted to cashbook.'
                    }
                </div>
                <button
                    type="button"
                    onClick={() => setBulkPostOpen(true)}
                    disabled={selectedIds.length === 0}
                    className="px-4 py-2 rounded-lg bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Post Selected to Cashbook{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
                </button>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-red-700 text-sm">{error}</div>
            )}

            {/* Table */}
            <div className="bg-white rounded-xl shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF5733]"></div>
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No C2B transactions found.</div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-3 py-2 text-left font-semibold text-gray-700 w-10">
                                            <input
                                                type="checkbox"
                                                aria-label="Select all rows on this page"
                                                checked={allSelectableSelected}
                                                ref={(input) => {
                                                    if (input) input.indeterminate = !allSelectableSelected && someSelectableSelected;
                                                }}
                                                onChange={toggleSelectAllOnPage}
                                                className="h-4 w-4 rounded border-gray-300 text-[#FF5733] focus:ring-[#FF5733]"
                                            />
                                        </th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Date & Time</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Receipt</th>
                                        <th className="px-4 py-2 text-right font-semibold text-gray-700">Amount</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Bill Ref</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Plate</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Payer</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Match</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Car Post</th>
                                        <th className="px-4 py-2 text-left font-semibold text-gray-700">Cashbook</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map(row => (
                                        <tr key={row.id} onClick={() => setSelectedRow(row)} className="border-b border-gray-100 hover:bg-orange-50 cursor-pointer">
                                            <td className="px-3 py-2 align-middle">
                                                <input
                                                    type="checkbox"
                                                    aria-label={`Select row ${row.trans_id}`}
                                                    checked={selectedRowMap.has(row.id)}
                                                    disabled={row.cb_posted === 'posted'}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        toggleRowSelection(row);
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="h-4 w-4 rounded border-gray-300 text-[#FF5733] focus:ring-[#FF5733] disabled:opacity-40"
                                                />
                                            </td>
                                            <td className="px-4 py-2 text-gray-700 whitespace-nowrap text-xs">{formatDateTime(row.trans_time)}</td>
                                            <td className="px-4 py-2 font-mono text-xs text-gray-700">{row.trans_id}</td>
                                            <td className="px-4 py-2 text-right font-semibold text-gray-900">{formatMoney(row.amount)}</td>
                                            <td className="px-4 py-2 text-gray-600 text-xs">{row.bill_ref_raw || '—'}</td>
                                            <td className="px-4 py-2 font-mono text-xs font-medium text-gray-800">{row.plate_no || '—'}</td>
                                            <td className="px-4 py-2 text-gray-600 text-xs">
                                                {[row.first_name, row.last_name].filter(Boolean).join(' ') || '—'}
                                            </td>
                                            <td className="px-4 py-2">
                                                {row.match_status === 'matched'
                                                    ? <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">Matched</span>
                                                    : <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">Unmatched</span>
                                                }
                                            </td>
                                            <td className="px-4 py-2">
                                                {row.post_status === 'posted'
                                                    ? <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">Posted</span>
                                                    : <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full">Pending</span>
                                                }
                                            </td>
                                            <td className="px-4 py-2">
                                                {row.cb_posted === 'posted'
                                                    ? <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">Posted</span>
                                                    : <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">Pending</span>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-600">
                            <span>{total === 0 ? '0 of 0' : `${offset + 1}–${Math.min(offset + LIMIT, total)} of ${total}`}</span>
                            <div className="flex gap-2">
                                <button onClick={() => { setSelectedIds([]); fetchData(offset - LIMIT); }} disabled={offset === 0}
                                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Previous
                                </button>
                                <button onClick={() => { setSelectedIds([]); fetchData(offset + LIMIT); }} disabled={offset + LIMIT >= total}
                                    className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                                    Next
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Totals / summary — reflects the current filters, not just this page */}
            {summary && (
                <div className="bg-white rounded-xl shadow p-4 mt-4">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-semibold text-gray-700">Summary (filtered)</h2>
                        <span className="text-xs text-gray-400">{summary.total_count.toLocaleString()} transaction{summary.total_count === 1 ? '' : 's'}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        {[
                            { label: 'Total', count: summary.total_count, amount: summary.total_amount },
                            { label: 'Matched', count: summary.matched_count, amount: summary.matched_amount },
                            { label: 'Unmatched', count: summary.unmatched_count, amount: summary.unmatched_amount },
                            { label: 'Posted to Car', count: summary.car_posted_count, amount: summary.car_posted_amount },
                            { label: 'Posted to Cashbook', count: summary.cb_posted_count, amount: summary.cb_posted_amount },
                        ].map((item) => (
                            <div key={item.label} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                                <div className="text-xs font-medium text-gray-500">{item.label}</div>
                                <div className="text-base font-bold text-gray-900 text-right mt-1">{formatMoney(item.amount)}</div>
                                <div className="text-xs text-gray-400 text-right">{item.count.toLocaleString()} txn{item.count === 1 ? '' : 's'}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>

        {selectedRow && (
            <C2BTransactionDrawer
                row={selectedRow}
                onClose={() => setSelectedRow(null)}
                onPosted={() => { setSelectedRow(null); fetchData(offset); }}
            />
        )}

        <C2BBulkPostModal
            isOpen={bulkPostOpen}
            rows={selectedRows}
            filters={{
                from,
                to,
                match_status: matchStatus,
                post_status: postStatus,
                cb_posted: cbPosted,
                search,
                week_key: selectedWeek,
            }}
            matchingTotal={total}
            matchingTotalAmount={totalAmount}
            onClose={() => setBulkPostOpen(false)}
            onSuccess={handleBulkPostSuccess}
        />

        <C2BRecoveryImportModal
            isOpen={showRecoveryImport}
            onClose={() => setShowRecoveryImport(false)}
            onImported={handleRecoveryImported}
        />
        </>
    );
};

export default C2BTransactions;
